import type { XrefPumps, Correlations } from "@/composables/forms/formTypes";
import { getMultiphaseTotalDynamicHeadFt } from "@/libs/calculations/pumps";
import type { BasicData } from "@/libs/calculations/fluidProperties/d";
import type { FluidPropertiesContext } from "../../fluidProperties/d";
import type { LagrangeInterpolationMapWithGroupings } from "@/composables/scenarioCalculations/lagrangeInterpolation";
import type { PumpHydraulicCalculations, StageCalculations } from "./types";
import { getFrequencyRatio } from "./basics";
import { processPumpStages } from "./stages";

export const getPumpHydraulicCalculations = (
  initialPressurePSIA: number,
  initialTemperatureF: number,
  xrefPump: XrefPumps[number],
  operatingRPM: number,
  gasHeadRerateRatio: number,
  basicData: BasicData,
  fluidPropertiesContext: FluidPropertiesContext,
  correlations: Correlations,
  pumpWearBHPCorrectionRatio: number,
  pumpWearHeadCorrectionRatio: number,
  isScenarioEspShore: boolean,
  customFluidProperties: LagrangeInterpolationMapWithGroupings | null,
  customViscosityProperties: LagrangeInterpolationMapWithGroupings | null,
  bubblePointPressurePSIAOverride?: number,
  dewPointPressurePSIAOverride?: number,
  condensateApi?: number,
  fluidViscosityCPOverride?: number | null
): PumpHydraulicCalculations => {
  const frequencyRatio = getFrequencyRatio(operatingRPM, isScenarioEspShore);

  // Loop through housing stages
  const totalStages = xrefPump.total_stages || 1;
  let traversedStages = 0;

  let intakePressurePSIA = initialPressurePSIA;
  let intakeTemperatureF = initialTemperatureF;
  let currentFluidPropertiesContext = fluidPropertiesContext;

  const calculationsForStages: StageCalculations[] = [];
  while (traversedStages < totalStages) {
    const stageCalculations = processPumpStages(
      intakeTemperatureF,
      intakePressurePSIA,
      xrefPump,
      frequencyRatio,
      gasHeadRerateRatio,
      basicData,
      currentFluidPropertiesContext,
      correlations,
      pumpWearBHPCorrectionRatio,
      pumpWearHeadCorrectionRatio,
      bubblePointPressurePSIAOverride,
      dewPointPressurePSIAOverride,
      condensateApi,
      fluidViscosityCPOverride,
      customFluidProperties,
      customViscosityProperties
    );
    calculationsForStages.push(stageCalculations);

    intakePressurePSIA = stageCalculations.dischargePressurePSIA;
    intakeTemperatureF = stageCalculations.dischargeTemperatureF;

    currentFluidPropertiesContext =
      stageCalculations.dischargeFluidPropertiesContext;

    traversedStages += 1;
  }

  // Set of stage calculations ran after first stage of pump
  const pumpIntakeStageCalculations = calculationsForStages[0];

  // Set of stage calculatins ran after last stage of pump
  const pumpDischargeStageCalculations =
    calculationsForStages[calculationsForStages.length - 1];

  // Total bhp generated by the pump
  const totalBrakeHP = calculationsForStages.reduce(
    (totalBrakeHP, stageCalculations) =>
      totalBrakeHP + stageCalculations.generatedPowerBrakeHP,
    0
  );
  // Total pressure generated in the pump (does not include intake pressure)
  const pumpGeneratedPressurePSIA = calculationsForStages.reduce(
    (totalPressurePSIA, stageCalculations) =>
      totalPressurePSIA + stageCalculations.generatedPressurePSIA,
    0
  );

  // We cannot just aggregate head across the pump due to the changing
  // of fluid density throughout the calcs. Must use discharge density
  // to convert it back afterwards (does not include lift from intake pressure)
  const pumpGeneratedLiftHeadFt = getMultiphaseTotalDynamicHeadFt(
    pumpGeneratedPressurePSIA,
    pumpDischargeStageCalculations?.dischargeFluidPropertiesContext
      .fluidProperties.fluidDensityLBMFT3 || 0
  );

  // This is using the pump discharge pressure to calculate head
  // This includes intake pressure
  const totalDynamicHeadFt = getMultiphaseTotalDynamicHeadFt(
    pumpDischargeStageCalculations?.dischargePressurePSIA || 0,
    pumpDischargeStageCalculations?.dischargeFluidPropertiesContext
      .fluidProperties.fluidDensityLBMFT3 || 0
  );

  const pumpIntakeFlowRateExceedsGraphMax = calculationsForStages.some(
    stageCalcs => stageCalcs.pumpIntakeFlowRateExceedsGraphMax
  );
  const averageStageFlowRateBBLD = calculationsForStages.length
    ? calculationsForStages.reduce(
        (sum, stageCalcs) => sum + stageCalcs.averageFlowRateBBLD,
        0
      ) / calculationsForStages.length
    : 0;

  return {
    intakeStageCalculations: pumpIntakeStageCalculations,
    dischargeStageCalculations: pumpDischargeStageCalculations,
    stageCalculations: calculationsForStages,
    averageStageFlowRateBBLD,
    totalBrakeHP,
    pumpGeneratedPressurePSIA,
    totalDynamicHeadFt,
    pumpGeneratedLiftHeadFt,
    gasAtIntakeRatio:
      pumpIntakeStageCalculations.intakeFluidPropertiesContext.fluidProperties
        .gasFlowVolumeRatio,
    gasAtDischargeRatio:
      pumpDischargeStageCalculations.dischargeFluidPropertiesContext
        .fluidProperties.gasFlowVolumeRatio,
    pumpIntakeFlowRateExceedsGraphMax
  };
};
